import { useEffect, useRef, useState } from "react";
import { Text } from "@cognigy-internal/ui-component-library";
import { loadRecaptcha } from "lib/scripts";

export interface RecaptchaHandle {
	reset(): void;
}

interface RecaptchaProps {
	siteKey: string;
	onToken: (token: string) => void;
	onReady?: (handle: RecaptchaHandle) => void;
	load?: typeof loadRecaptcha;
}

export function Recaptcha({ siteKey, onToken, onReady, load = loadRecaptcha }: RecaptchaProps) {
	const container = useRef<HTMLDivElement>(null);
	const onTokenRef = useRef(onToken);
	const onReadyRef = useRef(onReady);
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		onTokenRef.current = onToken;
		onReadyRef.current = onReady;
	});

	useEffect(() => {
		let cancelled = false;
		load()
			.then(grecaptcha => {
				const element = container.current;
				if (cancelled || !element) return;
				const widgetId = grecaptcha.render(element, {
					sitekey: siteKey,
					callback: token => onTokenRef.current(token),
					"expired-callback": () => onTokenRef.current(""),
					"error-callback": () => onTokenRef.current(""),
				});
				onReadyRef.current?.({
					reset: () => {
						grecaptcha.reset(widgetId);
						onTokenRef.current("");
					},
				});
			})
			.catch(() => {
				if (!cancelled) setFailed(true);
			});
		return () => {
			cancelled = true;
		};
	}, [siteKey, load]);

	return (
		<div className="min-h-[78px]">
			<div
				ref={container}
				data-testid="recaptcha"
			/>
			{failed && (
				<Text
					variant="body-sm"
					className="text-error"
					role="alert"
				>
					The captcha could not be loaded. Please disable content blockers for this page and reload.
				</Text>
			)}
		</div>
	);
}
